import React from 'react';
import { useParams } from 'react-router-dom';

import { transformNodesToNetworkMapClonotypes } from './networkMap.utils';
import { useEnsureSelectedClonotypeDefinition } from '../hooks/useEnsureSelectedClonotypeDefinition';
import { useFilteredCellsWithClonotype } from '../hooks/useFilteredCellsWithClonotype';
import { useGetCellsWithClonotype, useGetNodes } from '../store/store';
import { getClonotypeEnumFromDefinition } from '../utils';

/**
 * Fetches nodes + cells for the current experiment and transforms them into NetworkMapClonotype[]
 * (the shape used by the network-graph plot). RTK Query dedupes the underlying requests,
 * so this can safely be called from multiple components simultaneously.
 */
export function useNetworkMapClonotypes() {
  const { id: experimentId } = useParams<{ id: string }>();
  const selectedClonotypeDefinition = useEnsureSelectedClonotypeDefinition();

  const { data: cellData, isLoading: cellDataLoading } = useGetCellsWithClonotype(
    {
      id: experimentId!,
      clonotypeDefinition: getClonotypeEnumFromDefinition(selectedClonotypeDefinition!),
    },
    { skip: !experimentId || !selectedClonotypeDefinition },
  );

  const filteredData = useFilteredCellsWithClonotype({
    data: cellData,
    options: { skipClonotypeFilters: false },
  });

  const { data: nodeData, isLoading: nodeDataLoading } = useGetNodes(
    {
      id: experimentId!,
      clonotypeDefinition: getClonotypeEnumFromDefinition(selectedClonotypeDefinition!),
    },
    { skip: !experimentId || !selectedClonotypeDefinition },
  );

  const clonotypes = React.useMemo(() => {
    if (!nodeData || nodeDataLoading || !cellData || cellDataLoading) {
      return [];
    }
    return transformNodesToNetworkMapClonotypes(nodeData, filteredData);
  }, [nodeData, nodeDataLoading, cellData, cellDataLoading, filteredData]);

  const byClonotypeId = React.useMemo(() => {
    const m = new Map<number, (typeof clonotypes)[number]>();
    for (const c of clonotypes) {
      m.set(c.clonotypeId, c);
    }
    return m;
  }, [clonotypes]);

  return { clonotypes, byClonotypeId, isLoading: cellDataLoading || nodeDataLoading };
}
